import React, { type KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Can from '../../../components/Can';
import type { PickingItem, PickingListoResult } from '../../../services/pickingVentaService';
import { formatCurrency } from '../../../utils/formatters';
import { detenerTeclaLector, formatCantidadPicking, formatComprobantePicking } from '../pickingHelpers';

const lectorPaperProps = {
  onKeyDownCapture: detenerTeclaLector,
  onKeyUpCapture: detenerTeclaLector,
};

function ItemsPendientesList({ items }: { items: PickingItem[] }) {
  return (
    <List dense sx={{ maxHeight: 240, overflow: 'auto', bgcolor: '#fafafa', borderRadius: 1 }}>
      {items.map((item) => (
        <ListItem key={item.nroItem}>
          <ListItemText
            primary={`${item.codigoArticulo ?? ''} — ${item.descripcion ?? ''}`}
            secondary={`Pendiente: ${formatCantidadPicking(item.cantidadPendiente)} de ${formatCantidadPicking(item.cantidadPedida)}${item.um ? ` ${item.um}` : ''}`}
          />
        </ListItem>
      ))}
    </List>
  );
}

interface PickingParcialDialogProps {
  open: boolean;
  pendientes: PickingItem[];
  loading: boolean;
  mensaje?: string | null;
  onCancelar: () => void;
  onEnEspera: () => void;
}

export const PickingParcialDialog: React.FC<PickingParcialDialogProps> = ({
  open,
  pendientes,
  loading,
  mensaje,
  onCancelar,
  onEnEspera,
}) => (
  <Dialog
    open={open}
    disableEscapeKeyDown
    maxWidth="sm"
    fullWidth
    PaperProps={lectorPaperProps}
  >
    <DialogTitle>Picking incompleto</DialogTitle>
    <DialogContent>
      <Alert severity="warning" sx={{ mb: 2 }}>
        {mensaje?.trim() ||
          'Quedan artículos sin pickear. Cancelá lo pendiente o mandalo a un pedido en espera. El lector queda bloqueado hasta que elijas.'}
      </Alert>
      <ItemsPendientesList items={pendientes} />
    </DialogContent>
    <DialogActions>
      <Button
        color="inherit"
        onClick={onCancelar}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        Cancelar pendiente
      </Button>
      <Button
        variant="contained"
        color="warning"
        onClick={onEnEspera}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        En espera
      </Button>
    </DialogActions>
  </Dialog>
);

interface PickingLecturaErrorDialogProps {
  mensaje: string | null;
  onAceptar: () => void;
}

function aceptarConEnter(onAceptar: () => void) {
  return (event: ReactKeyboardEvent) => {
    if (event.repeat) return;
    if (event.key !== 'Enter' && event.code !== 'NumpadEnter') {
      detenerTeclaLector(event);
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    onAceptar();
  };
}

export const PickingLecturaErrorDialog: React.FC<PickingLecturaErrorDialogProps> = ({ mensaje, onAceptar }) => (
  <Dialog
    open={Boolean(mensaje)}
    disableEscapeKeyDown
    disableAutoFocus
    maxWidth="xs"
    fullWidth
    PaperProps={{
      onKeyDownCapture: aceptarConEnter(onAceptar),
      onKeyUpCapture: detenerTeclaLector,
    }}
  >
    <DialogTitle>Lectura rechazada</DialogTitle>
    <DialogContent>
      <Alert severity="error">{mensaje}</Alert>
    </DialogContent>
    <DialogActions>
      <Button variant="contained" color="error" onClick={onAceptar}>
        Aceptar
      </Button>
    </DialogActions>
  </Dialog>
);

interface PickingListoResultadoDialogProps {
  resultado: PickingListoResult | null;
  comprobante: string;
  onClose: () => void;
  onFacturar: (pedidoId: number) => void;
}

export const PickingListoResultadoDialog: React.FC<PickingListoResultadoDialogProps> = ({
  resultado,
  comprobante,
  onClose,
  onFacturar,
}) => {
  const espera = resultado?.pedidoFaltantes ?? resultado?.pedidoEnEspera;
  return (
    <Dialog open={Boolean(resultado)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Picking finalizado</DialogTitle>
      <DialogContent>
        <Alert severity="success" sx={{ mb: espera ? 2 : 0 }}>
          El pedido {comprobante} quedó {resultado?.parcial ? 'preparado en forma parcial' : 'preparado'} y
          ya está disponible para facturar.
        </Alert>
        {espera && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Se generó el pedido en espera {formatComprobantePicking(espera)}
              {espera.importeTotal != null ? ` por ${formatCurrency(espera.importeTotal)}` : ''} con lo pendiente:
            </Typography>
            <ItemsPendientesList items={espera.items ?? []} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Seguir con otro pedido</Button>
        {resultado && (
          <Can permiso="ventas.facturas.nuevo">
            <Button variant="contained" color="success" startIcon={<ReceiptIcon />} onClick={() => onFacturar(resultado.nroFormulario)}>
              Facturar ahora
            </Button>
          </Can>
        )}
      </DialogActions>
    </Dialog>
  );
};
